// Prefix match, not includes(): route names carry an i18n suffix
// (requests-id-chat___cs), but operator-requests-id-chat must not match.
const isTemporaryUserRoute = (route) => route.name?.startsWith('requests-id-chat')
  || route.name?.startsWith('requests-id-waiting');

export default defineNuxtRouteMiddleware(async(to, from) => {
  if (import.meta.client) {
    let isLoggedIn = useAuthStore().isLoggedIn;

    if (window.localStorage.getItem('token') && !isLoggedIn) {
      await useAuthStore().fetchUser();
      isLoggedIn = useAuthStore().isLoggedIn;
    }

    if (useError().value) { // for error page
      return abortNavigation();
    }

    if (isLoggedIn) {
      const auth = useAuthStore();
      const userHomeLink = auth.user?.is_operator ? '/operator/requests' : '/requests';

      // Restricted admins may only visit the routes of their granted modules.
      if (auth.isRestrictedAdmin) {
        // The landing-path guard avoids a redirect loop when a module maps to no
        // known route and adminLandingPath falls back outside allowedAdminRoutes.
        if (!auth.canAccessAdminRoute(to.path) && to.path !== auth.adminLandingPath) {
          return navigateTo(auth.adminLandingPath);
        }
        return;
      }

      // An anonymous SOS signs this browser in with ordinary tokens, so nothing
      // else keeps the temporary account off the client pages, the reservation
      // form included. Public stays open — an org tablet resets there (public.vue).
      if (auth.user?.is_temp && to.meta.layout === 'client' && !isTemporaryUserRoute(to)) {
        // Composables need the Nuxt instance, so both resolve before the await.
        const store = useNuxtApp().$store;
        const localePath = useLocalePath();
        const requestsState = store.state.client.requests;

        if (!requestsState.requests.length) {
          await store.dispatch('client/requests/getRequests');
        }

        const [request] = requestsState.requests;

        if (request) {
          return navigateTo(localePath(`/requests/${request.id}/chat`));
        }

        // getRequests resolves even on failure, so an empty list can also mean we
        // cannot tell which request is theirs. Fall back to their own list, never
        // to the page they asked for — that may be the reservation form.
        const listPath = localePath('/requests');

        if (to.path !== listPath) {
          return navigateTo(listPath);
        }
      }

      if (to.meta.layout === 'admin' && !auth.user?.is_admin) {
        return navigateTo(userHomeLink);
      }

      if (to.meta.layout === 'operator' && !auth.user?.is_operator) {
        return navigateTo(userHomeLink);
      }

      if (to.meta.layout === 'client' && auth.user?.is_operator) {
        return navigateTo(userHomeLink);
      }
    } else if (to.meta.layout !== 'public') {
      return navigateTo('/login');
    }
  }
});
